import { CommentElement } from "@/components/questionnaire/components/CommentElement.tsx";
import { QuestionElementProps } from "@/components/questionnaire/components/element.type.ts";
import {
  getFieldWithQuestionnaireSlug,
  getWriteToFieldEffectiveFromSlug,
  getWriteToFieldFields,
  getWriteToFieldRowsKey,
  getWriteToFieldSavedValue,
  getWriteToFieldSlug,
} from "@/components/questionnaire/components/write-to-field-element.utils.ts";
import useEmployeeCustomFieldsHook from "@/hooks/use-employee-custom-fields.hook.tsx";
import useCustomFieldsHook from "@/hooks/use-custom-fields.hook.tsx";
import { ApiCustomFieldValue, ApiField, ApiFieldTypes } from "@/types/custom-field.types.ts";
import { ApiEmployee } from "@/types/employees/employee.types.ts";
import {
  ApiFormElementWriteToFieldTableMode,
  ApiFormElementWriteToFieldTargetType,
} from "@/types/forms/form-edit.types.ts";
import { EmployeeFieldPermission, PermissionType } from "@/types/role.types.ts";
import {
  Button,
  ButtonIcon,
  FontAwesomeIcon,
  Select,
  Stack,
  Title,
  HtmlViewer,
} from "@skailer-com/uikit";
import { Fragment, useMemo } from "react";
import { Controller, UseFormReturn, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { WriteToFieldCompensationLines } from "@/components/questionnaire/components/WriteToFieldCompensationLines.tsx";
import { useOptionalQuestionnaireContext } from "@/components/questionnaire/questionnaire-context.ts";

interface WriteToFieldEmployeeEditFieldProps {
  field: ApiField;
  formHook: UseFormReturn;
  questionnaireEmployee: ApiEmployee;
  fieldValue?: ApiCustomFieldValue;
}

const WriteToFieldEmployeeEditField = ({
  field,
  formHook,
  questionnaireEmployee,
  fieldValue,
}: WriteToFieldEmployeeEditFieldProps) => {
  const { t } = useTranslation();
  const { getEmployeeEditFieldByType } = useEmployeeCustomFieldsHook([field.type]);
  const { getFieldByType, getFieldTitle } = useCustomFieldsHook();

  if (field.type === ApiFieldTypes.employee_compensation_lines) {
    return <WriteToFieldCompensationLines name={field.slug} />;
  }

  if (field.type === ApiFieldTypes.effective_from) {
    return getFieldByType({
      field,
      formHook,
      canView: true,
      canEdit: true,
      fieldValue: { date_value: new Date() },
    });
  }

  if (field.type === ApiFieldTypes.employee_termination_eligible_for_rehire) {
    return (
      <Controller
        control={formHook.control}
        name={field.slug}
        render={({ field: { value, onChange }, fieldState: { error } }) => (
          <Select
            label={getFieldTitle(field)}
            emptyTitle={t("common:not_selected")}
            options={[
              { id: "1", title: t("common:yes") },
              { id: "0", title: t("common:no") },
            ]}
            value={value == null ? null : value ? "1" : "0"}
            required={field.is_mandatory}
            isClearable={!field.is_mandatory}
            onChange={(nextValue) => {
              onChange(nextValue == null ? null : nextValue === "1");
            }}
            error={error?.message}
          />
        )}
      />
    );
  }

  const employee = fieldValue
    ? {
        ...questionnaireEmployee,
        custom_fields: questionnaireEmployee.custom_fields.map((customField) =>
          customField.field.id === field.id ? { ...customField, value: fieldValue } : customField
        ),
      }
    : questionnaireEmployee;

  return getEmployeeEditFieldByType(field, employee, formHook);
};

export const WriteToFieldElement = ({ element }: QuestionElementProps) => {
  const { t } = useTranslation();
  const formHook = useFormContext();
  const questionnaireContext = useOptionalQuestionnaireContext();
  const targetEmployee = questionnaireContext?.targetEmployee;
  const questionnaireForm = questionnaireContext?.questionnaireForm;

  const settings = element.write_to_field_settings;
  const fields = useMemo(() => {
    if (!settings) return [];

    return getWriteToFieldFields(element, settings);
  }, [element, settings]);

  const questionnaireEmployee = useMemo(() => {
    const employeeFieldPermissions = Object.values(EmployeeFieldPermission).reduce(
      (permissions, permission) => ({
        ...permissions,
        [permission]: PermissionType.edit,
      }),
      {}
    );

    return {
      ...targetEmployee,
      custom_fields: fields.map((field) => ({
        field,
        permission_value: PermissionType.edit,
        value: null,
      })),
      employee_field_permissions: employeeFieldPermissions,
      datetime_invited_at: targetEmployee?.datetime_invited_at ?? new Date(),
      datetime_last_online: targetEmployee?.datetime_last_online ?? new Date(),
    } as ApiEmployee;
  }, [fields, targetEmployee]);

  if (!settings) return null;

  const title = settings.title || t("surveys:write_to_field_title");
  const isCreateMultiple =
    settings.target_type === ApiFormElementWriteToFieldTargetType.table &&
    settings.table_mode === ApiFormElementWriteToFieldTableMode.create_multiple;
  const rowsKey = getWriteToFieldRowsKey(element.id);
  const watchedRowIndexes = formHook.watch(rowsKey);
  const rowIndexes =
    isCreateMultiple && Array.isArray(watchedRowIndexes) && watchedRowIndexes.length > 0
      ? watchedRowIndexes
      : [0];

  const handleAddRow = () => {
    const nextIndex = Math.max(...rowIndexes) + 1;
    formHook.setValue(rowsKey, [...rowIndexes, nextIndex], { shouldDirty: true });
  };

  const handleRemoveRow = (rowIndex: number) => {
    formHook.setValue(
      rowsKey,
      rowIndexes.filter((index) => index !== rowIndex),
      { shouldDirty: true }
    );

    fields.forEach((field) => {
      const slug = getWriteToFieldSlug(element.id, field.id, rowIndex, field.type);
      formHook.unregister(slug);
      formHook.unregister(`${slug}_currency`);
    });
    formHook.unregister(getWriteToFieldEffectiveFromSlug(element.id, rowIndex));
  };

  return (
    <Stack gap={"2xl"}>
      {(title || settings.description) && (
        <Stack gap="md">
          <Title header={title} size={"md"} required={settings.is_required} />
          {settings.description && <HtmlViewer content={settings.description} />}
        </Stack>
      )}

      <Stack gap={"md"}>
        {rowIndexes.map((rowIndex, entryIndex) => (
          <Stack key={rowIndex}>
            {isCreateMultiple && (
              <Stack direction={"horizontal"} items={"baseline"} justify={"between"}>
                <Title
                  size={"xs"}
                  header={t("surveys:write_to_field_entry_title", { number: entryIndex + 1 })}
                  paddingBottom
                  paddingTop={entryIndex > 0}
                />
                {entryIndex !== 0 && (
                  <ButtonIcon
                    variant={"secondary"}
                    icon={<FontAwesomeIcon icon={"fa-light fa-trash"} />}
                    onClick={() => handleRemoveRow(rowIndex)}
                  />
                )}
              </Stack>
            )}
            {fields.map((field) => (
              <Fragment key={`${rowIndex}_${field.type}_${field.id}`}>
                <WriteToFieldEmployeeEditField
                  field={getFieldWithQuestionnaireSlug(
                    field,
                    element.id,
                    field.is_mandatory,
                    rowIndex
                  )}
                  questionnaireEmployee={questionnaireEmployee}
                  formHook={formHook}
                  fieldValue={getWriteToFieldSavedValue(
                    questionnaireForm,
                    element.id,
                    rowIndex,
                    field
                  )}
                />
              </Fragment>
            ))}
          </Stack>
        ))}
        {isCreateMultiple && (
          <Stack direction={"horizontal"}>
            <Button type={"button"} variant={"secondary"} onClick={handleAddRow}>
              {t("surveys:write_to_field_add_another")}
            </Button>
          </Stack>
        )}
      </Stack>
      {settings.is_comment_enabled && <CommentElement element={element} />}
    </Stack>
  );
};
